import { BCMSRecord, Branding, DisabledPrompt, Link, useBCMSContext, useParams } from "@bcm-dev/bcms-vite";
import { TravelPlan, Trip } from "model";
import React, { useState } from "react";

import MainContentContainer, { MainContentHeader, MainContentSection } from "components/shared/containers/MainContentContainer";
import ControlToggleView from "components/shared/views/BaseToggleView/components/ControlToggleView";
import DestinationSelect from "components/ui/DestinationSelect";
import { SVGAsset } from "res";
import CountryOfResidenceWidget from "./widgets/CountryOfResidenceWidget";
import TravelPlanWidget from "./widgets/TravelPlanWidget";

import "./styles.css";

/**
 * @function LocationControlsPage
 * @return {React.Component}
 * @exported true
 * @description The main component for LocationControlsPage
 */

export default function LocationControlsPage(props) {
  const controller = useBCMSContext();
  const cardID = useParams().cardID ?? "0";

  BCMSRecord.globalControllerFor([ Trip, TravelPlan ], controller);

  const regionLocation = props.regionLocation ?? "Home";
  const trips = Trip.initAll()
    .filter(trip => {
      const plan = TravelPlan.initByID(trip.travelPlanID);
      return plan.card.id === cardID;
    }, [])
    .map(trip => {
      return (
        <TravelPlanWidget
          location={ trip.destinationName }
          status={ trip.status }
          dateRange={ `${ trip.departDate.format("M d") } - ${ trip.returnDate.format("M d") }` }
          flagIcon={ trip.flagIcon } />
      );
    }, []);

  return (
    <MainContentContainer className="location-controls-page" header={
      <MainContentHeader text="Location Controls" dataPoint="Location_Controls_Header" />
      
    }>
      
      <Branding id="locationcontrols_myregions_chw23" value="true">
        <LocationControlsToggleSection
          mainText="My Regions"
          subText="All in-store transactions are denied if merchant location is outside selected regions."
          dataPoint="My_Regions_Header"
          className="hide-chevron-icon" >

          <div className="region-row flex-container flex-horizontal align-center-container">
            <div className="set-width">
              <DisabledPrompt>
                <SVGAsset id="PencilIcon" className="left-inline-icon clickable" />
              </DisabledPrompt>
            </div>
            <div className="flex-width">
              <span>{regionLocation}</span>
            </div>
            <div className="set-width">
              <DisabledPrompt>
                <SVGAsset id="CircleMinusIcon" className="clickable" />
              </DisabledPrompt>
            </div>
          </div>
       

        <MainContentSection 
          text="Add Region" 
          icon={
            <Link to={ `/home/region/${ cardID }` }>
              <SVGAsset id="CirclePlusIcon" className="clickable" />
            </Link>
          }>
        </MainContentSection> 
        </LocationControlsToggleSection>
      </Branding>
    
      <Branding id="locationcontrols_international_chw23" value="true">
        <LocationControlsToggleSection
          mainText="Block International Transactions"
          subText="All international in-store transactions are denied outside selected countries. If you don't add any countries, all international in-store transactions are denied."
          dataPoint="Block_International_Header"
          className="hide-chevron-icon" >

            <Branding id="locationcontrols_country_residence_chw23" value="true">
              <CountryOfResidenceWidget />
            </Branding>
        </LocationControlsToggleSection>
   

      <Branding id="locationcontrols_addcountry_chw23" value="true">
        <MainContentSection 
          text="Add Country" 
          icon={
            <DisabledPrompt>
              <SVGAsset id="CirclePlusIcon" className="clickable" />
            </DisabledPrompt>
          }>
          {/* "UIDestinationSelect" component still needs to be built out. */}
          <DestinationSelect />
        </MainContentSection>
      </Branding>
      </Branding>
      <MainContentSection text="Travel Plans">
        { trips }
      </MainContentSection>

      <MainContentSection 
        text="Add Travel Plan" 
        icon={
          trips.length < 2
            ? <Link to={ `/home/securitytoken/receivetoken/${ cardID }/addtravelplan` }>
                <SVGAsset id="CirclePlusIcon" className="clickable" />
              </Link>

            : <DisabledPrompt>
                <Link to={ `/home/securitytoken/receivetoken/${ cardID }/addtravelplan` }>
                  <SVGAsset id="CirclePlusIcon" className="clickable" />
                </Link>
              </DisabledPrompt>
        }
        dataPoint="Add_Travel_Plan"
        >
        <DisabledPrompt>
          <Link className="tell-me-more-link">Tell me more</Link>
        </DisabledPrompt>
      </MainContentSection>
    </MainContentContainer>
  );
}

/**
 * @function LocationControlsToggleSection
 * @return {React.Component}
 * @exported false
 * @description Component to assign toggle functionality to the ControlToggleView and show hide content
 */
function LocationControlsToggleSection(props) {
  const [ state, setState ] = useState(props.checked||false);
  const toggle = value => setState(value);
  return (
    <>
      <ControlToggleView { ...props } onChange={ toggle } />
      { state && props.children }
    </>
  );
}